// Support portal — "Report a problem" (/support) and the ticket list
// (/support/tickets). The user attaches a chat / draft by picking it from their
// own history in a dialog (no ids to type); the one they came from (`?from=<path>`
// set by the user menu) is preselected, so the ticket links straight to the
// per-turn chat log. Who sees what is the backend's call (tenant:read to
// file, tenant:manage for own tenant's list, platform reach for every tenant).
// Look & feel follows the Claude Design handoff "Support pages restyle".
import { useState, type DragEvent, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Alert, Button, Dialog, DialogClose, LoadingState, PageHead, Pill, Tooltip } from "../components";
import { useSession } from "../auth/AuthProvider";
import { sessionAuthorized } from "../auth/capabilities";
import { useChatSessions } from "../data/chatQuery";
import { useDrafts } from "../data/draftsQuery";
import {
  useAttachmentUrl,
  useCloseSupportTicket,
  useSubmitSupportTicket,
  useSupportTickets,
  type SupportTicketDraft,
  type SupportTicketRow,
} from "../data/supportQuery";

const MAX_FILES = 5;
const ACCEPT = "image/*,video/*,.txt,.json,.md,.pdf";
const SUB = "Tell us what went wrong. Attach a screenshot, screen recording or exported chat if you have one.";

function activeTenantOf(session: ReturnType<typeof useSession>): string | null {
  return (
    session?.activeTenantId ??
    (session?.studioId != null ? `tenant-${session.studioId}` : null)
  );
}

/** Chat session id from `?chat=` and draft id from `/drafts/<id>` in the page the user came from. */
export function contextFrom(from: string): { chatSessionId: string; draftId: string } {
  const url = new URL(from, "http://x");
  const draft = /^\/drafts\/([^/]+)/.exec(url.pathname);
  return {
    chatSessionId: url.searchParams.get("chat") ?? "",
    draftId: draft !== null ? decodeURIComponent(draft[1]!) : "",
  };
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** "Just now", "5m ago", "1h ago", "Yesterday", "Oct 14". */
function relativeTime(iso: string, now = Date.now()): string {
  const t = new Date(iso);
  const mins = Math.floor((now - t.getTime()) / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  if (mins < 24 * 60) return `${Math.floor(mins / 60)}h ago`;
  if (mins < 48 * 60) return "Yesterday";
  return t.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function initials(who: string): string {
  const local = who.split("@")[0] ?? who;
  const parts = local.split(/[._\-\s]+/).filter((p) => p.length > 0);
  const s = parts.length >= 2 ? parts[0]![0]! + parts[1]![0]! : local.slice(0, 2);
  return s.toUpperCase();
}

// ── Icons (inline, currentColor) ────────────────────────────────────────
const svg = (d: ReactNode, size = 14): JSX.Element => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {d}
  </svg>
);
const IconChat = (s?: number) => svg(<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />, s);
const IconDoc = (s?: number) =>
  svg(
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
    </>,
    s
  );
const IconClip = (s?: number) =>
  svg(<path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />, s);
const IconUpload = (s?: number) =>
  svg(
    <>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <path d="m17 8-5-5-5 5" />
      <path d="M12 3v12" />
    </>,
    s
  );
const IconCheck = (s?: number) => svg(<path d="M20 6 9 17l-5-5" />, s);
const IconX = (s?: number) => svg(<path d="M18 6 6 18M6 6l12 12" />, s);
const IconChevron = (s?: number) => svg(<path d="m9 18 6-6-6-6" />, s);

// ── /support: Report a problem ──────────────────────────────────────────

export function Support(): JSX.Element {
  const session = useSession();
  const tenantId = activeTenantOf(session);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const from = params.get("from") ?? "";
  const submit = useSubmitSupportTicket();
  const blank = (): SupportTicketDraft => ({
    title: "",
    steps: "",
    expected: "",
    actual: "",
    pageUrl: from,
    ...contextFrom(from),
  });
  const [draft, setDraft] = useState<SupportTicketDraft>(blank);
  const [files, setFiles] = useState<File[]>([]);
  const [dragging, setDragging] = useState(false);
  // The user picks from their own history; ids never appear in the form.
  const chats = useChatSessions({ tenantId: tenantId ?? undefined, enabled: tenantId !== null });
  const drafts = useDrafts();

  const set = (k: keyof SupportTicketDraft) => (
    e: { target: { value: string } }
  ) => setDraft({ ...draft, [k]: e.target.value });

  const addFiles = (list: FileList | null): void => {
    if (list === null) return;
    setFiles((cur) => [...cur, ...Array.from(list)].slice(0, MAX_FILES));
  };

  if (tenantId === null) {
    return (
      <div className="sup">
        <PageHead eyebrow="Support" title="Report a problem" sub={SUB} />
        <Alert label="Select a workspace first">
          Tickets are filed against a workspace. Switch to one from the user menu.
        </Alert>
      </div>
    );
  }

  if (submit.isSuccess) {
    return (
      <div className="sup">
        <PageHead eyebrow="Support" title="Report a problem" sub={SUB} />
        <div className="sup-card sup-done" data-testid="support-success">
          <div className="sup-done__tile">{IconCheck(18)}</div>
          <h2 className="sup-done__title">Thanks — your report was filed</h2>
          <p className="sup-done__sub">
            Ticket <code>{submit.data.id}</code>. We&apos;ll follow up by email.
          </p>
          <div className="sup-done__actions">
            <Button
              variant="secondary"
              size="s"
              onClick={() => {
                submit.reset();
                setDraft(blank());
                setFiles([]);
              }}
            >
              Report another
            </Button>
            {from !== "" && (
              <Button variant="ghost" size="s" onClick={() => navigate(from)}>
                Back to where I was
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    submit.mutate({ tenantId, draft, files });
  };

  const onDrop = (e: DragEvent<HTMLLabelElement>): void => {
    e.preventDefault();
    setDragging(false);
    addFiles(e.dataTransfer.files);
  };

  return (
    <div className="sup">
      <PageHead eyebrow="Support" title="Report a problem" sub={SUB} />
      <form className="sup-card sup-form" onSubmit={handleSubmit} data-testid="support-form">
        <section className="sup-sec">
          <label className="sup-field">
            <span className="sup-field__row">
              <span className="sup-field__label">Title</span>
              <span className="sup-count">{draft.title.length} / 200</span>
            </span>
            <input
              className="sup-input sup-input--lg"
              value={draft.title}
              onChange={set("title")}
              required
              maxLength={200}
              placeholder="e.g. Apply on the review card did nothing"
            />
            <span className="sup-hint">One line. Required — everything else is optional.</span>
          </label>
        </section>

        <section className="sup-sec">
          <div className="sup-sec__label">What happened</div>
          <label className="sup-field">
            <span className="sup-field__label">Steps to reproduce</span>
            <textarea
              className="sup-input"
              rows={4}
              value={draft.steps}
              onChange={set("steps")}
              placeholder={"1. Open the draft\n2. Start a clause review\n3. Click Apply on § 9.2"}
            />
          </label>
          <div className="sup-pair">
            <label className="sup-field">
              <span className="sup-field__label">What you expected</span>
              <textarea className="sup-input" rows={3} value={draft.expected} onChange={set("expected")} />
            </label>
            <label className="sup-field">
              <span className="sup-field__label">What happened instead</span>
              <textarea className="sup-input" rows={3} value={draft.actual} onChange={set("actual")} />
            </label>
          </div>
        </section>

        <section className="sup-sec">
          <div className="sup-sec__label">Context</div>
          <p className="sup-sec__sub">Linking the chat lets us replay every turn instead of asking you.</p>
          <AttachPicker
            label="Chat"
            none="No chat attached"
            selectedId={draft.chatSessionId}
            items={(chats.data ?? []).map((c) => ({ id: c.id, title: c.title || "Untitled chat", when: c.updatedAt }))}
            loading={chats.isPending}
            onPick={(id) => setDraft({ ...draft, chatSessionId: id })}
          />
          <AttachPicker
            label="Draft"
            none="No draft attached"
            selectedId={draft.draftId}
            items={(drafts.data ?? []).map((d) => ({ id: d.id, title: d.title || "Untitled draft", when: d.updatedAt }))}
            loading={drafts.isPending}
            onPick={(id) => setDraft({ ...draft, draftId: id })}
          />
        </section>

        <section className="sup-sec">
          <div className="sup-sec__row">
            <div className="sup-sec__label">Attachments</div>
            <span className="sup-count">
              {files.length} of {MAX_FILES}
            </span>
          </div>
          {files.length < MAX_FILES && (
            <label
              className={`sup-drop${dragging ? " sup-drop--over" : ""}`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
            >
              <span className="sup-drop__icon">{IconUpload(18)}</span>
              <span className="sup-drop__main">
                <strong>Choose files</strong> or drop them here
              </span>
              <span className="sup-drop__sub">
                Screenshots, recordings, transcripts · images, video, .txt .json .md .pdf
              </span>
              <input
                className="sup-drop__input"
                type="file"
                multiple
                accept={ACCEPT}
                onChange={(e) => {
                  addFiles(e.target.files);
                  e.target.value = "";
                }}
              />
            </label>
          )}
          {files.length > 0 && (
            <ul className="sup-files">
              {files.map((f, n) => (
                <li key={`${f.name}-${n}`} className="sup-file">
                  <span className="sup-file__icon">{IconDoc(14)}</span>
                  <span className="sup-file__name">{f.name}</span>
                  <span className="sup-file__size">{formatSize(f.size)}</span>
                  <Button
                    variant="ghost"
                    size="xs"
                    aria-label={`Remove ${f.name}`}
                    onClick={() => setFiles(files.filter((_, i) => i !== n))}
                  >
                    {IconX(12)}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {submit.isError && (
          <div className="sup-sec sup-sec--flush">
            <Alert label="Couldn't send the report" tone="error">
              {submit.error.message}
            </Alert>
          </div>
        )}

        <footer className="sup-foot">
          <span className="sup-foot__meta">
            {draft.pageUrl !== "" && (
              <>
                Filed from <code>{draft.pageUrl}</code> ·{" "}
              </>
            )}
            we&apos;ll reply by email
          </span>
          <Button
            type="submit"
            variant="primary"
            className="sup-nowrap"
            disabled={draft.title.trim().length === 0 || submit.isPending}
          >
            {submit.isPending ? "Sending…" : "Send report"}
          </Button>
        </footer>
      </form>
    </div>
  );
}

type PickItem = { id: string; title: string; when?: string };

/** "Chat: <title> [Change…]" — a dialog lists the user's own chats / drafts. */
function AttachPicker(p: {
  label: string;
  none: string;
  selectedId: string;
  items: PickItem[];
  loading: boolean;
  onPick: (id: string) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const selected = p.items.find((i) => i.id === p.selectedId);
  const current = p.selectedId === "" ? p.none : (selected?.title ?? `This ${p.label.toLowerCase()}`);
  const isChat = p.label === "Chat";
  const pick = (id: string) => {
    p.onPick(id);
    setOpen(false);
  };
  return (
    <div className="sup-attach" data-testid={`support-pick-${p.label.toLowerCase()}`}>
      <span className={`sup-attach__tile${isChat ? " sup-attach__tile--chat" : ""}`}>
        {isChat ? IconChat(15) : IconDoc(15)}
      </span>
      <span className="sup-attach__text">
        <span className="sup-attach__label">{p.label}</span>
        <span className={`sup-attach__value${p.selectedId === "" ? " sup-attach__value--none" : ""}`}>
          {current}
        </span>
      </span>
      {p.selectedId !== "" && (
        <Button type="button" variant="ghost" size="xs" onClick={() => p.onPick("")}>
          Remove
        </Button>
      )}
      <Button type="button" variant="secondary" size="xs" className="sup-nowrap" onClick={() => setOpen(true)}>
        {p.selectedId === "" ? `Attach a ${p.label.toLowerCase()}…` : "Change…"}
      </Button>
      <Dialog
        open={open}
        onOpenChange={setOpen}
        title={`Attach a ${p.label.toLowerCase()}`}
        contentClassName="ds-dialog__content sup-pick"
        titleClassName="sup-pick__title"
      >
        <DialogClose asChild>
          <Button variant="bare" className="sup-pick__close" aria-label="Close">
            {IconX(14)}
          </Button>
        </DialogClose>
        {p.loading ? (
          <div className="sup-pick__empty">
            <LoadingState label="Loading…" />
          </div>
        ) : p.items.length === 0 ? (
          <p className="sup-pick__empty">Nothing here yet.</p>
        ) : (
          <ul className="sup-pick__list">
            {p.items.map((i) => (
              <li key={i.id}>
                <Button
                  type="button"
                  variant="bare"
                  className={`sup-pick__row${i.id === p.selectedId ? " sup-pick__row--on" : ""}`}
                  onClick={() => pick(i.id)}
                >
                  <span className="sup-pick__text">
                    <span className="sup-pick__name">{i.title}</span>
                    {i.when !== undefined && (
                      <span className="sup-pick__when">{new Date(i.when).toLocaleString()}</span>
                    )}
                  </span>
                  {i.id === p.selectedId && <span className="sup-pick__check">{IconCheck(14)}</span>}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Dialog>
    </div>
  );
}

// ── /support/tickets ────────────────────────────────────────────────────

export function SupportTickets(): JSX.Element {
  const session = useSession();
  // Platform staff see every tenant; anyone else their own tenant only.
  const allTenants = sessionAuthorized(session, "platform:manage_tenants", "platform");
  const ownTenant = activeTenantOf(session);
  const tenantId = allTenants ? null : ownTenant;
  const q = useSupportTickets(tenantId);
  const close = useCloseSupportTicket();
  const attachmentUrl = useAttachmentUrl();
  // Titles for the chat / draft a ticket points at, when it is one of ours.
  const chats = useChatSessions({ tenantId: ownTenant ?? undefined, enabled: ownTenant !== null });
  const drafts = useDrafts();
  const [openId, setOpenId] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);
  const openAttachment = (tid: string, id: string, n: number): void => {
    // Open the tab synchronously so the popup blocker allows it, then point it
    // at the short-lived signed URL.
    const win = window.open("", "_blank");
    attachmentUrl(tid, id, n).then(
      (url) => {
        if (win !== null) win.location.href = url;
      },
      (err: unknown) => {
        win?.close();
        setLinkError(err instanceof Error ? err.message : String(err));
      }
    );
  };

  if (q.isPending) return <LoadingState label="Loading tickets…" />;
  if (q.isError)
    return (
      <Alert label="Couldn't load tickets" tone="error">
        {q.error.message}
      </Alert>
    );

  const openCount = q.data.filter((t) => t.status === "open").length;
  const closedCount = q.data.length - openCount;
  const chatTitle = (id: string): string | undefined => chats.data?.find((c) => c.id === id)?.title || undefined;
  const draftTitle = (id: string): string | undefined => drafts.data?.find((d) => d.id === id)?.title || undefined;

  return (
    <>
      <PageHead
        eyebrow="Support"
        title="Support tickets"
        sub={allTenants ? "Problem reports from every workspace." : "Problems you've reported, and where each one stands."}
        cta={
          <div className="sup-counts">
            <span className="sup-counts__item">
              <span className="sup-dot sup-dot--open" />
              <strong>{openCount}</strong> open
            </span>
            <span className="sup-counts__item">
              <span className="sup-dot sup-dot--closed" />
              <strong>{closedCount}</strong> closed
            </span>
          </div>
        }
      />
      {(linkError !== null || close.isError) && (
        <Alert label="That didn't work" tone="error">
          {linkError ?? close.error?.message}
        </Alert>
      )}
      {q.data.length === 0 ? (
        <Alert label="No tickets yet">
          {allTenants ? (
            "Nobody has reported a problem."
          ) : (
            <>
              You haven&apos;t reported a problem. <Link to="/support">Report one</Link>.
            </>
          )}
        </Alert>
      ) : (
        <>
          <div className="sup-card sup-list" data-testid="support-tickets">
            <div className="sup-grid sup-list__head">
              <span>Report</span>
              <span>Reporter</span>
              <span>Context</span>
              <span>Filed</span>
              <span>Status</span>
            </div>
            {q.data.map((t) => {
              const key = `${t.tenantId}/${t.id}`;
              return (
                <TicketRow
                  key={key}
                  t={t}
                  open={openId === key}
                  onToggle={() => setOpenId(openId === key ? null : key)}
                  allTenants={allTenants}
                  own={t.tenantId === ownTenant}
                  chatTitle={t.chatSessionId !== undefined ? chatTitle(t.chatSessionId) : undefined}
                  draftTitle={t.draftId !== undefined ? draftTitle(t.draftId) : undefined}
                  closing={close.isPending}
                  onClose={() => close.mutate({ tenantId: t.tenantId, ticketId: t.id })}
                  onAttachment={(n) => openAttachment(t.tenantId, t.id, n)}
                />
              );
            })}
          </div>
          <p className="sup-list__foot">
            {q.data.length} {q.data.length === 1 ? "ticket" : "tickets"} · newest first · click a row for the full
            report
          </p>
        </>
      )}
    </>
  );
}

function TicketRow(p: {
  t: SupportTicketRow;
  open: boolean;
  onToggle: () => void;
  allTenants: boolean;
  /** The ticket is in the viewer's own workspace, so its chat / draft links resolve. */
  own: boolean;
  chatTitle: string | undefined;
  draftTitle: string | undefined;
  closing: boolean;
  onClose: () => void;
  onAttachment: (n: number) => void;
}): JSX.Element {
  const { t } = p;
  const who = t.email ?? t.uid;
  const preview = t.actual || t.expected || t.steps;
  const closed = t.status !== "open";
  const hasChat = t.chatSessionId !== undefined;
  const hasDraft = t.draftId !== undefined;
  return (
    <div className={`sup-row${p.open ? " sup-row--open" : ""}`}>
      {/* A div, not a button: the row holds the GitHub link, and a link
          cannot sit inside a button. Enter / Space toggle like a button. */}
      <div
        className="sup-grid sup-row__main"
        role="button"
        tabIndex={0}
        aria-expanded={p.open}
        onClick={p.onToggle}
        onKeyDown={(e) => {
          if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            p.onToggle();
          }
        }}
      >
        <span className="sup-row__chev">{IconChevron(14)}</span>
        <span className="sup-row__report">
          <span className="sup-row__title-line">
            <span className={`sup-row__title${closed ? " sup-row__title--closed" : ""}`}>{t.title}</span>
            {t.attachments.length > 0 && (
              <span className="sup-row__clip">
                {IconClip(12)}
                {t.attachments.length}
              </span>
            )}
          </span>
          <span className="sup-row__preview">
            {p.allTenants && <span className="sup-row__ws">{t.tenantId} ·</span>} {preview}
          </span>
        </span>
        <span className="sup-row__who">
          <span className="sup-avatar">{initials(who)}</span>
          <span className="sup-ellipsis">{who}</span>
        </span>
        <span className="sup-row__ctx">
          {hasChat && (
            <Tooltip content={p.chatTitle ?? "Chat attached"}>
              <span className="sup-chip sup-chip--chat">{IconChat(11)}Chat</span>
            </Tooltip>
          )}
          {hasDraft && (
            <Tooltip content={p.draftTitle ?? "Draft attached"}>
              <span className="sup-chip">{IconDoc(11)}Draft</span>
            </Tooltip>
          )}
          {!hasChat && !hasDraft && <span className="sup-muted">—</span>}
        </span>
        <span className="sup-row__filed">
          <Tooltip content={new Date(t.createdAt).toLocaleString()}>
            <span>{relativeTime(t.createdAt)}</span>
          </Tooltip>
        </span>
        <span className="sup-row__status">
          <Pill variant={closed ? "low" : "medium"}>
            {closed ? (
              <span className="sup-pill-ico">{IconCheck(10)}</span>
            ) : (
              <span className="sup-dot sup-dot--open sup-dot--sm" />
            )}
            {closed ? "Closed" : "Open"}
          </Pill>
          {t.github !== undefined && (
            <a
              className="sup-issue"
              href={t.github.url}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              #{t.github.issueNumber}
            </a>
          )}
        </span>
      </div>
      {p.open && (
        <div className="sup-row__panel">
          <div className="sup-cards">
            <ReportCard label="Steps to reproduce" text={t.steps} />
            <ReportCard label="Expected" text={t.expected} />
            <ReportCard label="What happened" text={t.actual} tone="bad" />
          </div>
          <div className="sup-actions">
            {t.attachments.map((a, n) => (
              <Button key={a.uri} size="xs" className="sup-nowrap" onClick={() => p.onAttachment(n)}>
                {IconDoc(12)} {a.filename} <span className="sup-muted">{formatSize(a.size)}</span>
              </Button>
            ))}
            {hasChat &&
              (p.own ? (
                <Link className="btn btn--xs sup-nowrap sup-link" to={`/home?chat=${encodeURIComponent(t.chatSessionId!)}`}>
                  Chat · {p.chatTitle ?? "open"} ↗
                </Link>
              ) : (
                <span className="sup-chip sup-chip--chat">{IconChat(11)}Chat attached</span>
              ))}
            {hasDraft &&
              (p.own ? (
                <Link className="btn btn--xs sup-nowrap" to={`/drafts/${encodeURIComponent(t.draftId!)}`}>
                  Draft · {p.draftTitle ?? "open"} ↗
                </Link>
              ) : (
                <span className="sup-chip">{IconDoc(11)}Draft attached</span>
              ))}
            <span className="sup-actions__spacer" />
            <code className="sup-id">{t.id}</code>
            {/* Closing is triage, so platform staff only (the API agrees). */}
            {!closed && p.allTenants && (
              <Button size="xs" className="sup-nowrap" disabled={p.closing} onClick={p.onClose}>
                Close ticket
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ReportCard(p: { label: string; text: string; tone?: "bad" }): JSX.Element {
  return (
    <div className={`sup-rcard${p.tone === "bad" ? " sup-rcard--bad" : ""}`}>
      <div className="sup-rcard__label">{p.label}</div>
      {p.text !== "" ? (
        <div className="sup-rcard__text">{p.text}</div>
      ) : (
        <div className="sup-rcard__text sup-muted">Not provided</div>
      )}
    </div>
  );
}
